import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import RHFTextArea from 'src/components/controls/tempRHFControls/RHFTextArea';
import Check from 'src/models/Check';
import { SelectItem } from 'src/models/common/SelectItem';
import Invoice from 'src/models/leasing/Invoice';
import useFetch from 'use-http';
import Card from 'src/components/common/Card.redesign';
import CardContainer from 'src/components/common/CardContainer.redesign';
import CheckOptions from 'src/models/CheckOptions';
import { ODataOptions } from 'src/util/ODataBuilder';
import Filter from 'src/components/common/Filter';
import * as _ from 'lodash';
import RecordingFee from 'src/models/RecordingFee';
import MathUtil from 'src/util/MathUtil';

interface ICheckOptionsDialogStateProps {
    invoice: Invoice;
    leaseNumber: string;
    leaseId: number;
    dialogName: string;
    clientNumber: number;
    projectNumber: number;
    clientId: number;
    projectId: number;
    checkOptions: CheckOptions;
    formatData: (data: Check) => Check;
    hideDialog: (reset: boolean) => void;
}

const CreateCheckDialog = (props: ICheckOptionsDialogStateProps) => {
    enum CheckTypes {
        LESSOR = 'LESSOR',
        MULTI_INVOICE = 'MULTI_INVOICE',
        BACKUP_WITHHOLDING = 'BACKUP_WITHHOLDING',
        IRS_WITHHOLDING = 'IRS_WITHHOLDING',
        RECORDING_FEES = 'RECORDING_FEES'
    }

    const checkTypeOptions = [
        { value: CheckTypes.LESSOR, label: 'Lessor Check (Direct)' },
        { value: CheckTypes.MULTI_INVOICE, label: 'Multi-Invoice Collection Check' },
        { value: CheckTypes.BACKUP_WITHHOLDING, label: 'Backup Withholding' },
        { value: CheckTypes.IRS_WITHHOLDING, label: 'IRS Check for Backup W/H' },
        { value: CheckTypes.RECORDING_FEES, label: 'Recording Fees Check' },
    ];

    const defaultValues: Check = {
        id: 0,
        checkType: '',
        leaseId: props.leaseId,
        companyId: 0,
        countyId: 0,
        invoiceTotal: props.invoice?.invoiceTotal,
        checkNumber: '',
        checkDate: null,
        totalAmount: props.invoice?.invoiceTotal,
        dateSent: null,
        glDate: null,
        voidDate: null,
        isVoid: false,
        voidReason: '',
        bonusAmount: props.invoice?.bonusAmount,
        otherAmount: props.invoice?.otherFee,
        rentalsAmount: props.invoice?.rentAmount,
        bankFeesAmount: props.invoice?.bankCharge,
        payee: '',
        address: '',
        city: '',
        stateId: 0,
        stateName: '',
        zip: '',
        memo: '',
        projectNumber: props.projectNumber,
        withholdingPercent: 28,
        withholding: parseFloat(MathUtil.cleanNumber((props.invoice?.invoiceTotal * .28), 5)),
        totalAfterWH: parseFloat(MathUtil.cleanNumber((props.invoice?.invoiceTotal * .72), 5)),
        leaseNumber: props.leaseNumber,
        invoiceNumber: props.invoice?.invoiceNumber,
        totalRemaining: props.invoice?.invoiceTotal,

        invoices: [],
        lease: null,
        recordingFees: [],
        withholdingChecks: []
    };

    const [checkData, setCheckData] = useState<Check>(defaultValues);

    const [accountTypes, setAccountTypes] = useState<SelectItem[]>([]);
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [selectedEntities, setSelectedEntities] = useState<boolean[]>([]);
    const [irsChecks, setIRSChecks] = useState<Check[]>([]);
    const [recordingFees, setRecordingFees] = useState<RecordingFee[]>([]);
    const [filters, setFilters] = useState('');
    const [buttonDisabled, setButtonDisabled] = useState<boolean>(true);

    const [checkForm, setCheckForm] = useState(<div />);
    const [showForm, setShowForm] = useState(false);
    const { register, getValues, setValue, handleSubmit, reset, errors } = useForm();
    const { get, put, post, response } = useFetch();

    const loadCheckData = async () => {
        // Load AccountTypes
        let accountTypesList: SelectItem[] = [
            { id: 1, name: 'Lease' },
            { id: 2, name: 'Leasehold' },
            { id: 3, name: 'Miscellaneous' }
        ];

        setAccountTypes(accountTypesList);

        // Set Initial Check Data
        setCheckData(defaultValues);
    };

    const loadInvoices = async () => {  
        // Load withholding invoices
        let rec = getValues();
        if (props.leaseId && props.leaseId > 0 && filters.length > 0 && (rec.checkType === CheckTypes.MULTI_INVOICE)) {
            let fullFilter = filters + ` and where eq leaseId,${props.leaseId}`;

            let data: Invoice[] = await get(`v1.0/clients/${props.clientId}/projects/${props.projectId}/invoice${fullFilter}`);

            if (response.ok) {
                setInvoices(data);
            } else {
                console.log('bad request', response, filters);
            }
        } else if (filters.length === 0) {
            setInvoices([]);
        }
    };

    const loadIRSRecords = async () => {
        if (props.clientId && props.clientId > 0) {
            let data: Check[] = await get(`v1.0/clients/${props.clientId}/projects/${props.projectId}/check/irs-records`);

            if (response.ok) {
                setIRSChecks(data);
            } else {
                setIRSChecks([]);
            }
        }
    };

    const loadRecordingFees = async () => {
        if (props.projectId && props.projectId > 0) {
            let rec = getValues();
            let recFeeFilter;
            if (filters.length > 0 && rec.checkType === CheckTypes.RECORDING_FEES) {
                recFeeFilter = filters + ` and where eq leaseId,${props.leaseId}`;
            } else {
                recFeeFilter = `?$filter=where eq projectId,${props.projectId}`;
            }
            recFeeFilter += ` and where eq noCheck,NULL`;

            let data: RecordingFee[] = await get(`v1.0/clients/${props.clientId}/projects/${props.projectId}/recordingFee${recFeeFilter}`);

            if (response.ok) {
                setRecordingFees(data);
            } else {
                setRecordingFees([]);
            }
        }
    };

    const isButtonDisabled = (): void => {
        let rec = getValues();
        switch (rec.checkType) {
            case CheckTypes.MULTI_INVOICE:
            case CheckTypes.IRS_WITHHOLDING:
            case CheckTypes.RECORDING_FEES:
                let disabled = true;
                selectedEntities.map(se => {
                    disabled = disabled && se !== true;
                });
                setButtonDisabled(disabled);
                break;
            case CheckTypes.BACKUP_WITHHOLDING:
            case CheckTypes.LESSOR:        
                setButtonDisabled(false);
                break;
            default:
                break;
        }
    };

    useEffect(() => {
        setCheckForm(
            <div>
                <div className="form-row">
                    <RHFInputBox name="leaseNumber" label="Lease #" register={register} errors={errors} />
                    <RHFInputBox name="invoiceNumber" label="Invoice #" register={register} errors={errors} />
                    <RHFInputBox name="invoiceTotal" label="Invoice Total" register={register} errors={errors} type="number" />
                </div>
                <div className="form-row">
                    <RHFInputBox name="checkNumber" label="Check #" register={register} errors={errors} />
                    <RHFInputBox name="checkDate" label="Check Date" register={register} errors={errors} type="date" />
                    <RHFInputBox name="totalRemaining" label="Total Remaining" register={register} errors={errors} type="number" />
                </div>
                <div className="form-row">
                    <RHFInputBox name="rentalsAmount" label="Rentals" register={register} errors={errors} type="number" />
                    <RHFInputBox name="bankFeesAmount" label="Bank Fees" register={register} errors={errors} type="number" />
                </div>
                <div className="form-row">
                    <RHFInputBox name="bonusAmount" label="Bonus" register={register} errors={errors} type="number" />
                    <RHFInputBox name="otherAmount" label="Other" register={register} errors={errors} type="number" />
                    <RHFInputBox name="totalAmount" label="Check Total" register={register} errors={errors} type="number" />
                </div>

                <div style={{ display: 'flex' }}>
                    <div style={{ width: '100%' }}>
                        <div className="form-row">
                            <RHFInputBox name="dueDate" label="Due Date" register={register} errors={errors} type="date" />
                            <RHFSelect
                                name="companyId"
                                label="Company"
                                options={props.checkOptions?.companies?.map(c => {
                                    return { value: c.id, label: c.name };
                                    })}
                                register={register}
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div style={{ width: '100%' }}>
                            <RHFTextArea name="payee" label="Payee" register={register} errors={errors} />
                        </div>
                    </div>
                </div>
                <div style={{ width: '100%' }}>
                    <div className="form-row">
                        <RHFInputBox name="memo" label="Memo" register={register} errors={errors} style={{ width: '100%' }} />
                    </div>
                </div>
                <div style={{ width: '100%' }}>
                    <div className="form-row">
                        <RHFInputBox name="clientNumber" label="Client Number" register={register} errors={errors} />
                        <RHFInputBox name="projectNumber" label="Project Number" register={register} errors={errors} />
                    </div>
                </div>
                <div className="form-row">
                    <RHFInputBox name="withholdingPercent" label="Withholding Percent" register={register} errors={errors} type="number" />
                    <RHFInputBox name="withholding" label="Withholding" register={register} errors={errors} type="number" />
                    <RHFInputBox name="totalAfterWH" label="Total After W/H" register={register} errors={errors} type="number" />
                </div>

                <div className="form-row">
                    <RHFInputBox name="address" label="Address" register={register} errors={errors} style={{ width: '100%' }} />
                </div>
                <div className="form-row">
                    <RHFInputBox name="city" label="City" register={register} errors={errors} />
                    <RHFSelect
                        name="stateId"
                        label="State"
                        options={props.checkOptions?.states?.map(s => {
                            return { value: s.id, label: s.name };
                            })}
                        register={register}
                    />
                    <RHFInputBox name="zipCode" label="zipCode" register={register} errors={errors} />
                </div>
                <div>
                    <Button text="Create" intent="primary" type="submit" />
                </div>

            </div>      
        );    
    }, []);

    useEffect(() => {
        loadCheckData();
        loadIRSRecords();
        loadRecordingFees();
    }, [props.invoice]);

    useEffect(() => {
        reset(checkData);
    }, [checkData]);

    useEffect(() => {
        loadInvoices();
        loadRecordingFees();
        setSelectedEntities([]);
    }, [filters]);

    useEffect(() => {
        displayCheckForm();
    }, [invoices, irsChecks, recordingFees, buttonDisabled, selectedEntities]);

    useEffect(() => {
        isButtonDisabled();
    }, [selectedEntities]);

    const displayInvoices = () => {
        if (filters.length > 0 && invoices?.length > 0) {
            return invoices.map(((invoice, key) => {
                let cardClass = selectedEntities[key] === true ? ' selected' : '';

                return (
                    <Card className={`small-input${cardClass}`} newContext={false} onClick={() => toggleSelectedEntities(key)}>
                        <div style={{ display: 'flex' }}>
                            <div className="mr-2" style={{ width: '100%' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <div style={{ padding: '1rem', width: '10%' }}>
                                        <div>{invoice.invoiceNumber ? invoice.invoiceNumber : 'N/A'}</div>
                                    </div>
                                    <div style={{ padding: '1rem', width: '10%' }}>
                                        <div>{invoice.collectNumber ? invoice.collectNumber : 'N/A'}</div>
                                    </div>
                                    <div style={{ padding: '1rem', width: '15%' }}>
                                        <div>{invoice.leaseNumber}</div>
                                    </div>
                                    <div style={{ padding: '1rem', width: '10%' }}>
                                        <div>{invoice.projectNumber}</div>
                                    </div>
                                    <div style={{ padding: '1rem', width: '10%' }}>
                                        <div>{invoice.bonusAmount}</div>
                                    </div>
                                    <div style={{ padding: '1rem', width: '10%' }}>
                                        <div>{invoice.rentAmount}</div>
                                    </div>
                                    <div style={{ padding: '1rem', width: '10%' }}>
                                        <div>{invoice.bankCharge}</div>
                                    </div>
                                    <div style={{ padding: '1rem', width: '10%' }}>
                                        <div>{invoice.otherFee}</div>
                                    </div>
                                    <div style={{ padding: '1rem', width: '15%' }}>
                                        <div>{invoice.lessorName}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card>
                );
            }));
        } else if (filters.length <= 0) {
            return <div>Select Filters for Invoice</div>;
        } else {
            return <div>No Current Records</div>;
        }
    };

    const displayIRSRecords = () => {
        if (irsChecks?.length > 0) {
            return irsChecks?.map((check, key) => {
                check.withholding = Math.round(100 * check.totalAmount / ((100 / check.withholdingPercent) - 1)) / 100;
                let cardClass = selectedEntities[key] === true ? ' selected' : '';

                return (
                    <div key={key}>
                        <Card className={`small-input${cardClass}`} newContext={false} onClick={() => toggleSelectedEntities(key)}>
                            <div style={{ display: 'flex' }}>
                                <div className="mr-2" style={{ width: '100%' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <div style={{ padding: '1rem', width: '25%' }}>
                                            <div>{check.lease?.leaseNumber ? check.lease.leaseNumber : 'N/A'}</div>
                                        </div>
                                        <div style={{ padding: '1rem', width: '25%' }}>
                                            <div>{check.invoices[0]?.invoiceNumber ? check.invoices[0].invoiceNumber : 'N/A'}</div>
                                        </div>
                                        <div style={{ padding: '1rem', width: '20%' }}>
                                            <div>{check.withholding}</div>
                                        </div>
                                        <div style={{ padding: '1rem', width: '30%' }}>
                                            <div>{check.lease?.originalLessor ? check.lease.originalLessor : 'N/A'}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </div>
                );
            });
        } else {
            return <div>No Current Records</div>;
        }
    };

    const displayRecordingFeesRecords = () => {
        if (recordingFees?.length > 0) {
            return recordingFees?.map(((rf, key) => {
                let cardClass = selectedEntities[key] === true ? ' selected' : '';

                return (
                    <Card className={`small-input${cardClass}`} newContext={false} onClick={() => toggleSelectedEntities(key)}>
                        <div style={{ display: 'flex' }}>
                            <div className="mr-2" style={{ width: '100%' }}>
                                <div style={{ display: 'flex' }}>
                                    <div style={{ padding: '1rem', width: '35%' }}>
                                        <div>Lease Number: {rf.leaseNumber ? rf.leaseNumber : 'N/A'}</div>
                                    </div>
                                    <div style={{ padding: '1rem', width: '25%' }}>
                                        <div>Invoice Number: {rf.invoiceNumber ? rf.invoiceNumber : 'N/A'}</div>
                                    </div>
                                    <div style={{ padding: '1rem', width: '15%' }}>
                                        <div>State: {props?.checkOptions?.states?.find(st => st.id === rf.stateId)?.abbreviation || 'N/A'}</div>
                                    </div>
                                    <div style={{ padding: '1rem', width: '20%' }}>
                                        <div>County: {props?.checkOptions?.counties?.find(st => st.id === rf.countyId)?.name || 'N/A'}</div>
                                    </div>
                                </div>
                                <div style={{ display: 'flex' }}>
                                    <div style={{ padding: '1rem', width: '30%' }}>
                                        <div>Recording Fee: {rf.recordingFeeAmount}</div>
                                    </div>
                                    <div style={{ padding: '1rem', width: '30%' }}>
                                        <div>Instrument: {rf.indexTypeName}</div>
                                    </div>
                                    <div style={{ padding: '1rem', width: '35%' }}>
                                        <div>Lessor Name: {rf.lessorName}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card>
                );
            }));
        } else {
            return <div>No Current Records</div>;
        }
    };

    const toggleSelectedEntities = (key: number) => {
        let rec = getValues();
        console.log(rec);

        let newEntities = [...selectedEntities];
        newEntities[key] = !newEntities[key];

        if (rec.checkType === CheckTypes.IRS_WITHHOLDING) {
            let newTotal = 0;
            newEntities.forEach((ce, index) => {
                if (ce === true) {
                    newTotal += irsChecks[index].withholding;
                }
            });
            setValue('totalAmount', newTotal);
        } else if (rec.checkType === CheckTypes.RECORDING_FEES) {
            let newTotal = 0;
            newEntities.forEach((ce, index) => {
                if (ce === true) {
                    newTotal += recordingFees[index].recordingFeeAmount;
                }
            });
            setValue('totalAmount', newTotal);
        } else if (rec.checkType === CheckTypes.MULTI_INVOICE) {
            let newTotal = 0;
            newEntities.forEach((ce, index) => {
                if (ce === true) {
                    newTotal += invoices[index].invoiceTotal;
                }
            });
            console.log(`Multi-Invoice Total: ${newTotal}`);
            setValue('totalAmount', newTotal);
        }

        setSelectedEntities(newEntities);
    };

    const updateWHTotals = () => {
        let rep = getValues();

        let total = (parseFloat(rep.bonusAmount) + parseFloat(rep.otherAmount) + parseFloat(rep.bankFeesAmount) + parseFloat(rep.rentalsAmount));

        // Totals after withholding are for reference only. These changes will not reflect stored values.
        let withholding = Math.round(parseFloat(rep.withholdingPercent) * total) / 100;
        let newTotal = Math.round(100 * (total - withholding)) / 100;

        setValue('totalAmount', total);
        setValue('withholding', withholding);
        setValue('totalAfterWH', (newTotal));
    };

    const updateFilter = (options: ODataOptions) => {
        const stringOptions = options.toString();
        searchUpdate(stringOptions);
    };

    const searchUpdate = useCallback(
        _.debounce((filter: string) => {
            setFilters(filter);
        }, 800),
        []
    );

    const updateCheckForm = () => {
        setValue('totalAmount', 0);
        let newForm = checkData;
        newForm.totalAmount = 0;
        setCheckData(newForm);
        displayCheckForm();
    };

    const displayCheckForm = () => {
        let rec = getValues();

        // Lessor Direct Form
        let lessorInfo = (
            <div>
                <div className="form-row">
                    <RHFInputBox name="leaseNumber" label="Lease #" register={register} errors={errors} />
                    <RHFInputBox name="invoiceNumber" label="Invoice #" register={register} errors={errors} />
                    <RHFInputBox name="invoiceTotal" label="Invoice Total" register={register} errors={errors} type="number" />
                </div>
                <div className="form-row">
                    <RHFInputBox name="checkNumber" label="Check #" register={register} errors={errors} />
                    <RHFInputBox name="checkDate" label="Check Date" register={register} errors={errors} type="date" />
                    <RHFInputBox name="totalRemaining" label="Total Remaining" register={register} errors={errors} type="number" />
                </div>
                <div className="form-row">
                    {/* tslint:disable-next-line */}
                    <RHFInputBox name="rentalsAmount" label="Rentals" onChange={() => updateWHTotals()} register={register} errors={errors} type="number" />
                    {/* tslint:disable-next-line */}
                    <RHFInputBox name="bankFeesAmount" label="Bank Fees" onChange={() => updateWHTotals()} register={register} errors={errors} type="number" />
                </div>
                <div className="form-row">
                    {/* tslint:disable-next-line */}
                    <RHFInputBox name="bonusAmount" label="Bonus" onChange={() => updateWHTotals()} register={register} errors={errors} type="number" />
                    {/* tslint:disable-next-line */}
                    <RHFInputBox name="otherAmount" label="Other" onChange={() => updateWHTotals()} register={register} errors={errors} type="number" />
                    {/* tslint:disable-next-line */}
                    <RHFInputBox name="totalAmount" label="Check Total" onChange={() => updateWHTotals()} register={register} errors={errors} type="number" />
                </div>
            </div>
        );

        let collectionsInfo = (
            <div>
                <div style={{ display: 'flex' }}>
                    <div style={{ width: '100%' }}>
                        <div className="form-row">
                            <RHFInputBox name="checkNumber" label="Check #" register={register} errors={errors} />
                            <RHFInputBox name="checkDate" label="Check Date" register={register} errors={errors} type="date" />
                            <RHFInputBox name="totalAmount" label="Check Total" register={register} errors={errors} type="number" disabled={true} />
                        </div>
                        <div className="form-row">
                            <RHFInputBox name="dueDate" label="Due Date" register={register} errors={errors} type="date" />
                            <RHFSelect
                                name="companyId"
                                label="Company"
                                options={props.checkOptions?.companies?.map(c => {
                                    return { value: c.id, label: c.name };
                                    })}
                                register={register}
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div style={{ width: '100%' }}>
                            <RHFTextArea name="payee" label="Payee" register={register} errors={errors} />
                        </div>
                    </div>
                </div>
                <div style={{ width: '100%' }}>
                    <div className="form-row">
                        <RHFInputBox name="memo" label="Memo" register={register} errors={errors} style={{ width: '100%' }} />
                    </div>
                </div>
                <div style={{ width: '100%' }}>
                    <Filter
                        filterItems={[
                            { group: 1, text: 'Collect Number', orderName: 'collectNumber', orderDirection: 'descending' },
                            { group: 1, text: 'Check Number', orderName: 'checkNumber', orderDirection: 'descending' },
                            { group: 1, text: 'Invoice Number', orderName: 'invoiceNumber', orderDirection: 'descending' },
                            { group: 1, text: 'Invoice Due Date', orderName: 'invoiceDueDate', orderDirection: 'descending', type: 'date' },
                        ]}
                        onFilterChanged={updateFilter}
                    />
                    <div style={{ display: 'flex' }}>
                        <div className="mr-2" style={{ width: '100%' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <div style={{ padding: '1rem', width: '10%' }}>
                                    <div>Invoice Number</div>
                                </div>
                                <div style={{ padding: '1rem', width: '10%' }}>
                                    <div>Collect Number</div>
                                </div>
                                <div style={{ padding: '1rem', width: '15%' }}>
                                    <div>Lease Number</div>
                                </div>
                                <div style={{ padding: '1rem', width: '10%' }}>
                                    <div>Project Number</div>
                                </div>
                                <div style={{ padding: '1rem', width: '10%' }}>
                                    <div>Bonus</div>
                                </div>
                                <div style={{ padding: '1rem', width: '10%' }}>
                                    <div>Rental</div>
                                </div>
                                <div style={{ padding: '1rem', width: '10%' }}>
                                    <div>Bank</div>
                                </div>
                                <div style={{ padding: '1rem', width: '10%' }}>
                                    <div>Other</div>
                                </div>
                                <div style={{ padding: '1rem', width: '15%' }}>
                                    <div>Lessor Name</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <CardContainer showNextPagePanel={false}>
                        <div style={{ minHeight: '10rem', maxHeight: '30rem', overflow: 'scroll' }}>{displayInvoices()}</div>
                    </CardContainer>
                </div>
            </div>
        );

        let backupIRSInfo = (
            <div>
                <div style={{ display: 'flex' }}>
                    <div style={{ width: '100%' }}>
                        <div className="form-row">
                            <RHFInputBox name="checkNumber" label="Check #" register={register} errors={errors} />
                            <RHFInputBox name="checkDate" label="Check Date" register={register} errors={errors} type="date" />
                            <RHFInputBox name="totalAmount" label="Check Total" register={register} errors={errors} type="number" disabled={true} />
                        </div>
                        <div className="form-row">
                            <RHFSelect
                                name="companyId"
                                label="Company"
                                options={props.checkOptions?.companies?.map(c => {
                                    return { value: c.id, label: c.name };
                                })}
                                register={register}
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div style={{ width: '100%' }}>
                            <RHFTextArea name="payee" label="Payee" register={register} errors={errors} />
                        </div>
                    </div>
                </div>
                <div style={{ width: '100%' }}>
                    <div className="form-row">
                        <RHFInputBox name="clientNumber" label="Client Number" register={register} errors={errors} />
                        <RHFInputBox name="projectNumber" label="Project Number" register={register} errors={errors} />
                    </div>
                </div>
                <div style={{ width: '100%' }}>
                    <div style={{ display: 'flex' }}>
                        <div className="mr-2" style={{ width: '100%' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <div style={{ padding: '1rem', width: '25%' }}>
                                    <div>Lease Number</div>
                                </div>
                                <div style={{ padding: '1rem', width: '25%' }}>
                                    <div>Invoice Number</div>
                                </div>
                                <div style={{ padding: '1rem', width: '20%' }}>
                                    <div>W/H Amt</div>
                                </div>
                                <div style={{ padding: '1rem', width: '30%' }}>
                                    <div>LessorName</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <CardContainer showNextPagePanel={false}>
                        <div style={{ minHeight: '10rem', maxHeight: '30rem', overflow: 'scroll' }}>{displayIRSRecords()}</div>
                    </CardContainer>
                </div>
            </div>
        );

        let backupWHInfo = (
            <div className="form-row">
                <RHFInputBox name="withholdingPercent" label="Withholding Percent" onChange={() => updateWHTotals()} register={register} errors={errors} type="number" />
                <RHFInputBox name="withholding" label="Withholding" register={register} errors={errors} type="number" disabled={true} />
                <RHFInputBox name="totalAfterWH" label="Total After W/H" register={register} errors={errors} type="number" disabled={true} />
            </div>
        );

        let recordingFeeInfo = (
            <div>
                <div style={{ width: '100%' }}>
                    <div className="form-row">
                        <RHFInputBox name="projectNumber" label="Project Number" register={register} errors={errors} />
                        <RHFInputBox name="checkNumber" label="Check Number" register={register} errors={errors} />
                        <RHFInputBox name="checkDate" label="Check Date" register={register} errors={errors} type="date" />
                    </div>
                    <div className="form-row">
                        <RHFSelect
                            name="stateId"
                            label="State"
                            options={props.checkOptions?.states?.map(c => {
                                return { value: c.id, label: c.name };
                                })}
                            register={register}
                            style={{ width: '100%' }}
                        />
                        <RHFSelect
                            name="countyId"
                            label="County"
                            options={props.checkOptions?.counties?.map(c => {
                                return { value: c.id, label: c.name };
                                })}
                            register={register}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div className="form-row" style={{ width: '100%' }}>
                        <RHFTextArea name="payee" label="Payee" register={register} errors={errors} />
                    </div>
                </div>
                <div style={{ width: '100%' }}>
                    <div className="form-row">
                        <RHFInputBox name="totalAmount" label="Check Total" register={register} errors={errors} type="number" disabled={true}/>
                        <RHFSelect
                            name="accountTypeId"
                            label="Account Type"
                            options={accountTypes.map(c => {
                                return { value: c.id, label: c.name };
                                })}
                            register={register}
                            style={{ width: '100%' }}
                        />
                        <RHFSelect
                            name="companyId"
                            label="Company"
                            options={props.checkOptions?.companies?.map(c => {
                                return { value: c.id, label: c.name };
                                })}
                            register={register}
                            style={{ width: '100%' }}
                        />
                    </div>
                </div>
                <div style={{ width: '100%' }}>
                    <div className="form-row">
                        <RHFInputBox name="memo" label="Memo" register={register} errors={errors} style={{ width: '100%' }} />
                    </div>
                </div>
                <Filter
                    filterItems={[
                        { group: 1, text: 'County', orderName: 'county', orderDirection: 'descending' },
                        { group: 1, text: 'State', orderName: 'state', orderDirection: 'descending' },
                        { group: 1, text: 'Invoice Number', orderName: 'invoiceNumber', orderDirection: 'descending' },
                        { group: 1, text: 'Lease Number', orderName: 'leaseNumber', orderDirection: 'descending' },
                    ]}
                    onFilterChanged={updateFilter}
                />
                <div style={{ width: '100%' }}>
                    <CardContainer showNextPagePanel={false}>
                        <div style={{ minHeight: '10rem', maxHeight: '30rem', overflow: 'scroll' }}>{displayRecordingFeesRecords()}</div>
                    </CardContainer>
                </div>
            </div>
        );

        let payeeInfo = (
            <div>
                <div className="form-row">
                    <RHFSelect
                        name="companyId"
                        label="Company"
                        options={props.checkOptions?.companies?.map(c => {
                            return { value: c.id, label: c.name };
                            })}
                        register={register}
                        style={{ width: '100%' }}
                    />
                </div>
                <div style={{ width: '100%' }}>
                    <RHFTextArea name="payee" label="Payee" register={register} errors={errors} />
                </div>
                <div className="form-row">
                    <RHFInputBox name="address" label="Address" register={register} errors={errors} style={{ width: '100%' }} />
                </div>
                <div className="form-row">
                    <RHFInputBox name="city" label="City" register={register} errors={errors} />
                    <RHFSelect
                        name="stateId"
                        label="State"
                        options={props.checkOptions?.states?.map(s => {
                            return { value: s.id, label: s.name };
                            })}
                        register={register}
                    />
                    <RHFInputBox name="zipCode" label="zipCode" register={register} errors={errors} />
                </div>
                <div className="form-row">
                    <RHFInputBox name="memo" label="Memo" register={register} errors={errors} style={{ width: '100%' }} />
                </div>
            </div>
        );

        let submitButton = (
            <div>
                <div>
                    <Button text="Create" intent="primary" type="submit" disabled={buttonDisabled} />
                </div>
            </div>
        );

        let fullForm = <div />;

        switch (rec.checkType) {
            case (CheckTypes.LESSOR):
                fullForm = (
                    <div>
                        {lessorInfo}
                        {payeeInfo}
                        {submitButton}
                    </div>
                );
                break;
            case (CheckTypes.BACKUP_WITHHOLDING):
                fullForm = (
                    <div>
                        {lessorInfo}
                        {backupWHInfo}
                        {payeeInfo}
                        {submitButton}
                    </div>
                );
                break;
            case (CheckTypes.IRS_WITHHOLDING):
                fullForm = (
                    <div>
                        {backupIRSInfo}
                        {submitButton}
                    </div>
                );
                break;
            case (CheckTypes.MULTI_INVOICE):
                fullForm = (
                    <div>
                        {collectionsInfo}
                        {submitButton}
                    </div>
                );
                break;
            case (CheckTypes.RECORDING_FEES):
                fullForm = (
                    <div>
                        {recordingFeeInfo}
                        {submitButton}
                    </div>
                );
                break;
            default:
                break;
        }

        setShowForm(true);
        setCheckForm(fullForm);
        isButtonDisabled();
    };

    const onSubmit = async (check: Check) => {
        check = props.formatData(check);
        check.checkTypeId = check.checkType ? parseInt((props.checkOptions?.checkTypes?.find(ct => ct.name === check.checkType)).id + '', 10) : 0;

        switch (check.checkType) {
            case CheckTypes.BACKUP_WITHHOLDING:
            case CheckTypes.LESSOR:
                if (props.invoice) {
                    checkData.invoices = [];
                    checkData.invoices.push(props.invoice);
                }
                break;
            case CheckTypes.MULTI_INVOICE:
                checkData.invoices = [];
                selectedEntities.forEach((se, index) => {
                    if (se === true) {
                        checkData.invoices.push(invoices[index]);
                    }
                });
                break;
            case CheckTypes.IRS_WITHHOLDING:
                checkData.withholdingChecks = [];
                selectedEntities.forEach((se, index) => {
                    if (se === true) {
                        checkData.withholdingChecks.push(irsChecks[index]);
                    }
                });
                break;
            case CheckTypes.RECORDING_FEES:
                checkData.recordingFees = [];
                selectedEntities.forEach((se, index) => {
                    if (se === true) {
                        checkData.recordingFees.push(recordingFees[index]);
                    }
                });
                break;
            default:
                break;
        }

        check.invoices = checkData.invoices;
        check.recordingFees = checkData.recordingFees;
        check.withholdingChecks = checkData.withholdingChecks;

        if (check.id === 0) {
            await post(`v1.0/clients/${props.clientNumber}/projects/${props.projectNumber}/check`, check);

            if (response.ok) {
                props.hideDialog(true);
            }
        } else {
            await put(`v1.0/clients/${props.clientNumber}/projects/${props.projectNumber}/check`, check);

            if (response.ok) {
                props.hideDialog(true);
            }
        }
    };

    const onCloseDialog = () => {
        setSelectedEntities([]);
        setFilters('');
        setCheckForm(<div />);
        setShowForm(false);
    };

    return (
        <Dialog dialogName={props.dialogName} title={`Select Check Type`} onCloseDialog={onCloseDialog}>
            <div className="mb-1 mt-1">
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="mr-2">
                        <div style={{ display: 'flex' }}>
                            <div style={{ padding: '1rem', width: '50%' }}>
                                <RHFSelect
                                    name="checkType"
                                    label="Check Type"
                                    options={props.checkOptions?.checkTypes?.map(ct => {
                                        return { value: ct.name, label: checkTypeOptions.find(co => co.value === ct.name).label };
                                    })}
                                    register={register}
                                />
                            </div>
                            <div style={{ padding: '1rem', width: '50%' }}>
                                <Button text="Load Form" intent="primary" onClick={updateCheckForm}/>
                            </div>
                        </div>
                    </div>
                    <hr />
                    <div className="mr-2">
                        {showForm && (checkForm)}
                    </div>
                </form>
            </div>
        </Dialog>
    );
};

export default CreateCheckDialog;


// WEBPACK FOOTER //
// ./src/components/invoicing/components/CreateCheckDialog.tsx